import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Stack,
  Button,
  Divider,
  Chip,
  TableContainer,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link as MLink,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { InquiryAPI } from "../../../../../api/Inquiry";
import { useAuth } from "../../../Auth/AuthContext";
import { useGlobalSnackbar } from "../../../../GlobalFunction/GlobalSnackbarProvider";
import { getLangFromCookie } from "../../../../../Utils/LangCookie";

// ✅ use the same resolver as ProductCard/ProductDetail pages
import { resolveAssetUrl } from "../../../../../lib/http";

const font = {
  ZH: "Line Seed TW",
  EN: "Line Seed Sans",
  JP: "Line Seed JP",
};

const PLACEHOLDER_IMG = "/img/empty.webp";

function normalizeLang(l) {
  const s = String(l || "")
    .toUpperCase()
    .trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

// ✅ critical: all inquiry pages must resolve the same stable userID
function resolveUserID(u) {
  return (
    u?.id ||
    u?.ID ||
    u?.user?.id ||
    u?.user?.ID ||
    u?.userID ||
    u?.userId ||
    u?.user?.userID ||
    u?.user?.userId ||
    u?.sehoUserID ||
    u?.user?.sehoUserID ||
    u?.uid ||
    u?.user?.uid ||
    u?.memberID ||
    u?.memberId ||
    u?.user?.memberID ||
    u?.user?.memberId ||
    u?.username ||
    u?.user?.username ||
    u?.email ||
    u?.user?.email ||
    ""
  );
}

/** Try to extract URL from string/object like ProductCard */
function toUrl(v) {
  if (!v) return undefined;
  if (typeof v === "string") return v;

  if (typeof v === "object") {
    if (v.url) return v.url;
    if (v.image) return v.image;
    if (v.path) return v.path;

    if (v.sourceUrl) return v.sourceUrl;
    if (v.mediaItemUrl) return v.mediaItemUrl;

    if (v.node && (v.node.sourceUrl || v.node.mediaItemUrl))
      return v.node.sourceUrl || v.node.mediaItemUrl;
  }
  return undefined;
}

// ✅ Normalize "images" from whatever backend shape you have (WP / legacy)
function extractImages(row) {
  const raw =
    row?.images || row?.product?.images || row?.product?.main?.images || [];

  const list = Array.isArray(raw) ? raw : [];
  const urls = list
    .map(toUrl)
    .filter(Boolean)
    .map((u) => resolveAssetUrl(String(u)))
    .filter(Boolean);

  if (!urls.length) {
    const one =
      toUrl(row?.image) ||
      toUrl(row?.imageUrl) ||
      toUrl(row?.productImage) ||
      toUrl(row?.product?.image) ||
      null;

    if (one) return [resolveAssetUrl(String(one))];
  }

  return urls;
}

/* ------------------------------ Gallery Dialog ------------------------------ */

function ImageGalleryDialog({ open, onClose, title, images = [] }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const safeImages = useMemo(() => {
    const uniq = [];
    const seen = new Set();
    for (const u of images || []) {
      const s = String(u || "");
      if (!s) continue;
      if (seen.has(s)) continue;
      seen.add(s);
      uniq.push(s);
    }
    return uniq.length ? uniq : [PLACEHOLDER_IMG];
  }, [images]);

  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!open) return;
    setActiveIdx(0);
  }, [open, safeImages.length]);

  const mainSrc =
    safeImages[Math.min(activeIdx, safeImages.length - 1)] || PLACEHOLDER_IMG;

  const scrollerRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const dragRef = useRef({
    active: false,
    pointerId: null,
    captured: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });

  const DRAG_THRESHOLD = 6;

  const checkScrollable = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setIsScrollable(el.scrollWidth > el.clientWidth + 2);
  };

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(checkScrollable);
  }, [open, safeImages.length]);

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const el = scrollerRef.current;
    if (!el) return;

    dragRef.current.active = true;
    dragRef.current.pointerId = e.pointerId;
    dragRef.current.captured = false;
    dragRef.current.startX = e.clientX;
    dragRef.current.startScrollLeft = el.scrollLeft;
    dragRef.current.moved = false;
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const el = scrollerRef.current;
    if (!el) return;

    const dx = e.clientX - dragRef.current.startX;

    if (!dragRef.current.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      dragRef.current.moved = true;

      if (!dragRef.current.captured) {
        try {
          e.currentTarget.setPointerCapture(dragRef.current.pointerId);
          dragRef.current.captured = true;
        } catch {}
      }
    }

    el.scrollLeft = dragRef.current.startScrollLeft - dx;

    try {
      e.preventDefault();
    } catch {}
  };

  const onPointerUp = (e) => {
    if (!dragRef.current.active) return;

    dragRef.current.active = false;

    if (dragRef.current.captured) {
      try {
        e.currentTarget.releasePointerCapture(dragRef.current.pointerId);
      } catch {}
    }

    dragRef.current.pointerId = null;
    dragRef.current.captured = false;

    requestAnimationFrame(checkScrollable);
  };

  const safeThumbClick = (fn) => {
    if (dragRef.current.moved) return;
    fn?.();
  };

  const THUMB = isMdUp ? 86 : 76;
  const GAP = isMdUp ? 12 : 10;

  useEffect(() => {
    if (!open) return;
    const el = scrollerRef.current;
    if (!el) return;
    const node = el.querySelector(`[data-thumb-idx="${activeIdx}"]`);
    if (!node) return;

    const left = node.offsetLeft;
    const w = node.offsetWidth;
    const target = left - (el.clientWidth / 2 - w / 2);
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [activeIdx, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          position: "relative",
          overflow: "visible",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: "absolute",
          top: { xs: -40, sm: -50 },
          right: { xs: -10, sm: 0 },
          zIndex: 10,
          color: "primary",
          width: { xs: 42, sm: 46 },
          height: { xs: 42, sm: 46 },
          "&:hover": { bgcolor: "secondary" },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 26 }} />
      </IconButton>

      <DialogTitle sx={{ pr: 2, fontWeight: 700 }}>
        <Typography
          sx={{
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "wrap",
          }}
        >
          {title || "Images"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, pb: 2 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            height: { xs: 260, sm: 360, md: 440 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={mainSrc}
            alt="product"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.style.display = "none";
            }}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />

          {safeImages.length > 1 && (
            <>
              <IconButton
                onClick={() =>
                  setActiveIdx(
                    (p) => (p - 1 + safeImages.length) % safeImages.length,
                  )
                }
                sx={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(255,255,255,0.85)",
                  boxShadow: 1,
                }}
              >
                <ChevronLeftRounded />
              </IconButton>

              <IconButton
                onClick={() => setActiveIdx((p) => (p + 1) % safeImages.length)}
                sx={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(255,255,255,0.85)",
                  boxShadow: 1,
                }}
              >
                <ChevronRightRounded />
              </IconButton>
            </>
          )}

          {safeImages.length > 1 && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                bottom: 10,
                transform: "translateX(-50%)",
                px: 1.2,
                py: 0.4,
                borderRadius: 999,
                bgcolor: "rgba(0,0,0,0.55)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {activeIdx + 1}/{safeImages.length}
            </Box>
          )}
        </Box>

        {safeImages.length > 1 && (
          <Box sx={{ mt: 1.5 }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: THUMB + 18,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                ref={scrollerRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                sx={{
                  flex: 1,
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                  touchAction: "pan-x",
                  userSelect: "none",
                  cursor: isScrollable ? "grab" : "default",
                  "&:active": { cursor: isScrollable ? "grabbing" : "default" },
                  px: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: `${GAP}px`,
                    py: 1,
                    minWidth: "100%",
                  }}
                >
                  {safeImages.map((src, i) => {
                    const selected = i === activeIdx;
                    return (
                      <Box
                        key={`${src}-${i}`}
                        data-thumb-idx={i}
                        onClick={() => safeThumbClick(() => setActiveIdx(i))}
                        sx={{
                          flex: "0 0 auto",
                          width: THUMB,
                          height: THUMB,
                          borderRadius: 1.5,
                          border: "2px solid",
                          borderColor: selected ? "primary.main" : "divider",
                          bgcolor: "#fff",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Box
                          component="img"
                          src={src}
                          alt={`thumb-${i}`}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.style.display = "none";
                          }}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ Page ------------------------------ */

export default function InquiryCartPage() {
  const { showSnackbar } = useGlobalSnackbar();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // ✅ FIX: initialize from cookie so first load uses correct language
  const [lang, setLang] = useState(() => normalizeLang(getLangFromCookie()));

  const nav = useNavigate();
  const loc = useLocation();
  const { user, loading: authLoading } = useAuth();

  // ✅ fix: userID must be resolved, not just user.userID
  const userID = useMemo(() => {
    const uid = resolveUserID(user);
    return uid ? String(uid) : null;
  }, [user]);

  const [loading, setLoading] = useState(false);
  const [inq, setInq] = useState(null);
  const items = inq?.items || [];

  const [confirmRowOpen, setConfirmRowOpen] = useState(false);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [pendingDeleteProductID, setPendingDeleteProductID] = useState(null);
  const [pendingDeleteProductName, setPendingDeleteProductName] = useState("");

  const [imgDlgOpen, setImgDlgOpen] = useState(false);
  const [imgDlgTitle, setImgDlgTitle] = useState("");
  const [imgDlgImages, setImgDlgImages] = useState([]);

  const openImageDialog = (row) => {
    const imgs = extractImages(row);
    setImgDlgImages(imgs);
    setImgDlgTitle(row?.productName || row?.productID || "Images");
    setImgDlgOpen(true);
  };

  useEffect(() => {
    if (!authLoading && !user) {
      nav("/auth", { replace: false, state: { redirectTo: loc.pathname } });
    }
  }, [authLoading, user, nav, loc.pathname]);

  // ✅ FIX: request sequence guard to prevent race-condition overwriting language
  const loadSeqRef = useRef(0);

  async function load(uid, L = lang) {
    if (authLoading) return;

    const safeUid = String(uid || "").trim();
    if (!safeUid) {
      setInq(null);
      setLoading(false);
      return;
    }

    const seq = ++loadSeqRef.current;
    setLoading(true);

    try {
      const { inquiry } = await InquiryAPI.getCurrentInquiryFromUser(safeUid, {
        lang: normalizeLang(L),
      });

      if (seq !== loadSeqRef.current) return;

      setInq(inquiry || null);
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      console.error("Current inquiry load failed:", e);
      setInq(null);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }

  // ✅ Listen language change -> reload inquiry so product names switch correctly
  useEffect(() => {
    const syncLang = () => {
      const c = normalizeLang(getLangFromCookie());
      setLang(c);

      if (authLoading) return;

      if (userID) {
        load(userID, c);
      } else {
        setInq(null);
        setLoading(false);
      }
    };

    syncLang();
    window.addEventListener("app:languageChanged", syncLang);
    return () => window.removeEventListener("app:languageChanged", syncLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, userID]);

  const changeQty = (productID, qty) => {
    setInq((prev) =>
      !prev
        ? prev
        : {
            ...prev,
            items: (prev.items || []).map((it) =>
              it.productID === productID ? { ...it, qty } : it,
            ),
          },
    );
  };

  const removeRow = async (productID) => {
    if (!inq || !userID) return;
    try {
      await InquiryAPI.deleteItem(inq.inquiryID, productID, { lang });
      showSnackbar(confirmText.rowDeleted[lang], "success");
      await load(userID, lang);
      window.dispatchEvent(
        new CustomEvent("inquiry:updated", {
          detail: { reason: "deleted", productID },
        }),
      );
    } catch (e) {
      showSnackbar(confirmText.rowDeleteFail[lang], "error");
      await load(userID, lang);
    }
  };

  const commitQtyChanges = async () => {
    if (!inq || !userID) return;
    try {
      await InquiryAPI.updateInquiry({
        userID,
        inquiryID: inq.inquiryID,
        items: (inq.items || []).map(({ productID, qty }) => ({
          productID,
          qty: Number(qty || 0),
        })),
        lang,
      });
      showSnackbar(confirmText.qtyUpdated[lang], "success");
      window.dispatchEvent(
        new CustomEvent("inquiry:updated", { detail: { reason: "updated" } }),
      );
      await load(userID, lang);
    } catch (e) {
      showSnackbar(confirmText.qtyUpdateFail[lang], "error");
    }
  };

  const goReference = () => nav("/商品資訊");
  const goStep2 = () => nav("/詢價/查看");

  const deleteInquiry = async () => {
    if (!inq) return;
    try {
      await InquiryAPI.deleteInquiry(inq.inquiryID, { lang });
      showSnackbar(confirmText.allDeleted[lang], "success");
    } catch (e) {
      showSnackbar(confirmText.allDeleteFail[lang], "error");
    } finally {
      await load(userID, lang);
      window.dispatchEvent(
        new CustomEvent("inquiry:updated", { detail: { reason: "cleared" } }),
      );
    }
  };

  const askDeleteRow = (row) => {
    if (!row?.productID) return;
    setPendingDeleteProductID(row.productID);
    setPendingDeleteProductName(row.productName || "");
    setConfirmRowOpen(true);
  };

  const confirmDeleteRow = async () => {
    const pid = pendingDeleteProductID;
    setConfirmRowOpen(false);
    setPendingDeleteProductID(null);
    setPendingDeleteProductName("");
    if (!pid) return;
    await removeRow(pid);
  };

  const askDeleteAll = () => {
    if (!inq) return;
    setConfirmAllOpen(true);
  };

  const confirmDeleteAll = async () => {
    setConfirmAllOpen(false);
    await deleteInquiry();
  };

  const title = {
    ZH: "詢價表",
    EN: "Inquiry",
    JP: "見積り依頼書",
  };

  const step1 = {
    ZH: "確認您的詢價產品",
    EN: "Confirm your inquiry product",
    JP: "見積り製品の確認",
  };

  const step2 = {
    ZH: "填寫詢價資料 & 送出詢價單",
    EN: "Fill in details & submit inquiry",
    JP: "必要な情報の入力&依頼書の送信",
  };

  const info = {
    ZH: "以下是您所欲詢價的產品，如欲變更產品數量，請於數量欄位輸入正確數字，然後點選「更改數量」； 如欲將產品由詢價車刪除，請點選該列的刪除鈕即可。",
    EN: "The following are the products you wish to inquire about. To change the quantity, please enter the correct number in the quantity field and then click “Change Quantity”. To delete a product from the inquiry cart, please click the delete button in that column.",
    JP: "現在お見積り依頼に含まれている製品は以下の通りです。数量を変更する場合は、数量欄に正しい数値を入力し「再計算する」をクリックしてください。製品を変更する場合は、対象行の「削除」ボタンを押してください。",
  };

  const column = {
    no: { ZH: "No.", EN: "No.", JP: "No." },
    image: { ZH: "圖片", EN: "Image", JP: "画像" },
    series: { ZH: "產品型號", EN: "Series Number", JP: "型番" },
    name: { ZH: "產品名稱", EN: "Product", JP: "品名" },
    quantity: { ZH: "數量", EN: "Qty", JP: "数量" },
    delete: { ZH: "刪除", EN: "Delete", JP: "削除" },
  };

  const empty = {
    ZH: "目前沒有商品。",
    EN: "Currently there are no product",
    JP: "商品未選択",
  };

  const viewImg = {
    ZH: "看圖片",
    EN: "view image",
    JP: "画像を表示",
  };

  const buttonText = {
    other: {
      ZH: "參考其它產品",
      EN: "check other products",
      JP: "他の商品も見にいく",
    },
    saveChange: {
      ZH: "更改數量",
      EN: "Save Changes",
      JP: "再計算する",
    },
    cart: {
      ZH: "詢價",
      EN: "Inquire",
      JP: "確認画面へ進む",
    },
    bulkDelete: {
      ZH: "清空/刪除整張詢價單",
      EN: "Delete All",
      JP: "見積依頼リストを一括削除",
    },
  };

  const bottomText = {
    inquireID: {
      ZH: "詢價單號:",
      EN: "Inquiry Number:",
      JP: "見積番号:",
    },
    time: {
      ZH: "建立時間:",
      EN: "Date Created:",
      JP: "作成日時:",
    },
  };

  const confirmText = {
    confirmTitle: {
      ZH: "確認刪除",
      EN: "Confirm Deletion",
      JP: "削除の確認",
    },
    confirmAllTitle: {
      ZH: "確認清空",
      EN: "Confirm Delete All",
      JP: "一括削除の確認",
    },
    confirmRowBody: {
      ZH: (name) => `確定要刪除這個商品嗎？${name ? `\n（${name}）` : ""}`,
      EN: (name) =>
        `Are you sure you want to delete this item?${name ? `\n(${name})` : ""}`,
      JP: (name) =>
        `この商品を削除してもよろしいですか？${name ? `\n（${name}）` : ""}`,
    },
    confirmAllBody: {
      ZH: "確定要清空/刪除整張詢價單嗎？此操作無法復原。",
      EN: "Are you sure you want to delete the entire inquiry list? This action cannot be undone.",
      JP: "見積リストを空にしますか？",
    },
    cancel: {
      ZH: "取消",
      EN: "Cancel",
      JP: "キャンセル",
    },
    delete: {
      ZH: "刪除",
      EN: "Delete",
      JP: "削除",
    },
    deleteAll: {
      ZH: "全部刪除",
      EN: "Delete All",
      JP: "すべて削除",
    },
    rowDeleted: {
      ZH: "已刪除該商品",
      EN: "Item deleted",
      JP: "商品を削除しました",
    },
    rowDeleteFail: {
      ZH: "刪除失敗，請稍後再試",
      EN: "Delete failed. Please try again later.",
      JP: "削除に失敗しました。しばらくしてから再度お試しください。",
    },
    qtyUpdated: {
      ZH: "已更新商品數量",
      EN: "Quantity updated",
      JP: "数量を更新しました",
    },
    qtyUpdateFail: {
      ZH: "更新商品數量失敗",
      EN: "Failed to update quantity",
      JP: "数量の更新に失敗しました",
    },
    allDeleted: {
      ZH: "已刪除整張詢價單",
      EN: "Inquiry list deleted",
      JP: "見積依頼リストを削除しました",
    },
    allDeleteFail: {
      ZH: "刪除詢價單失敗，請稍後再試",
      EN: "Failed to delete inquiry list. Please try again later.",
      JP: "一括削除に失敗しました。しばらくしてから再度お試しください。",
    },
  };

  const goToProduct = (row) => {
    const parentID =
      row?.parentID || row?.product?.parentID || row?.productParentID || null;
    const variantID = row?.productID || null;

    if (!parentID || !variantID) return;

    nav(
      `/商品資訊/${encodeURIComponent(parentID)}?variant=${encodeURIComponent(
        variantID,
      )}`,
      {
        state: {
          fromBreadcrumbs: [{ label: "商品資訊", href: "b2b/商品資訊" }],
        },
      },
    );
  };

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
      <Typography
        variant="h3"
        align="center"
        sx={{
          fontWeight: 700,
          fontFamily: font[lang],
          color: "#F5A400",
          mb: 2,
        }}
      >
        {title[lang]}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          mb: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            bgcolor: "#f6f6f6",
            minWidth: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontFamily: font[lang] }}
          >
            Step1 <Chip color="primary" size="small" sx={{ ml: 1 }} />
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: "text.secondary",
              fontFamily: font[lang],
            }}
          >
            {step1[lang]}
          </Typography>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            bgcolor: "#f6f6f6",
            minWidth: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontFamily: font[lang] }}
          >
            Step2 <Chip size="small" sx={{ ml: 1 }} />
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: "text.secondary",
              fontFamily: font[lang],
            }}
          >
            {step2[lang]}
          </Typography>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "#ecf5ff" }}>
        <Typography
          variant="body2"
          color="text.secondary"
          fontFamily={font[lang]}
        >
          {info[lang]}
        </Typography>
      </Paper>

      {isMdUp ? (
        <Paper variant="outlined">
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="medium" sx={{ minWidth: 760 }}>
              <TableHead sx={{ bgcolor: "#fafafa" }}>
                <TableRow>
                  <TableCell width={80}>{column.no[lang]}</TableCell>
                  <TableCell width={120}>{column.image[lang]}</TableCell>
                  <TableCell width={170}>{column.series[lang]}</TableCell>
                  <TableCell>{column.name[lang]}</TableCell>
                  <TableCell width={220}>{column.quantity[lang]}</TableCell>
                  <TableCell width={120} align="center">
                    {column.delete[lang]}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {items.map((row, idx) => {
                  const imgs = extractImages(row);
                  const thumb = imgs[0] || PLACEHOLDER_IMG;

                  return (
                    <TableRow key={row.productID}>
                      <TableCell>{idx + 1}</TableCell>

                      <TableCell>
                        <Tooltip title={viewImg[lang]} placement="top">
                          <Box
                            component="button"
                            onClick={() => openImageDialog(row)}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              cursor: "pointer",
                            }}
                          >
                            <Box
                              component="img"
                              src={thumb}
                              alt={row.productName || row.productID}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.style.display = "none";
                              }}
                              sx={{
                                width: 78,
                                height: 58,
                                objectFit: "contain",
                                display: "block",
                                p: 0.5,
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>

                      <TableCell>{row.productID}</TableCell>

                      <TableCell>
                        <MLink
                          component="button"
                          underline="hover"
                          onClick={() => goToProduct(row)}
                          sx={{
                            fontWeight: 400,
                            fontFamily: font[lang],
                            textAlign: "left",
                            cursor: "pointer",
                            color: "primary.main",
                          }}
                        >
                          {row.productName}
                        </MLink>
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          value={row.qty}
                          type="number"
                          inputProps={{ min: 1 }}
                          onChange={(e) =>
                            changeQty(row.productID, e.target.value)
                          }
                          sx={{ width: 160 }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => askDeleteRow(row)}
                        >
                          <DeleteOutlineRounded />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {items.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{ py: 4, color: "text.secondary" }}
                    >
                      {empty[lang]}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Stack spacing={1.25}>
          {items.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{ p: 2, textAlign: "center", color: "text.secondary" }}
            >
              {empty[lang]}
            </Paper>
          ) : (
            items.map((row, idx) => {
              const imgs = extractImages(row);
              const thumb = imgs[0] || PLACEHOLDER_IMG;

              return (
                <Paper key={row.productID} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, fontFamily: font[lang] }}
                      >
                        {column.no[lang]}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, fontFamily: font[lang] }}
                      >
                        {idx + 1}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ gap: 1 }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: "nowrap", fontFamily: font[lang] }}
                      >
                        {column.image[lang]}
                      </Typography>

                      <Box
                        component="button"
                        onClick={() => openImageDialog(row)}
                        style={{
                          border: "none",
                          background: "transparent",
                          padding: 0,
                          cursor: "pointer",
                        }}
                      >
                        <Box
                          component="img"
                          src={thumb}
                          alt={row.productName || row.productID}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.style.display = "none";
                          }}
                          sx={{
                            width: 88,
                            height: 64,
                            objectFit: "contain",
                            display: "block",
                            borderRadius: 1,
                            p: 0.5,
                          }}
                        />
                      </Box>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ gap: 1 }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: "nowrap", fontFamily: font[lang] }}
                      >
                        {column.series[lang]}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontFamily: font[lang],
                          textAlign: "right",
                          flex: 1,
                          wordBreak: "break-all",
                        }}
                      >
                        {row.productID}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ gap: 1 }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: "nowrap", fontFamily: font[lang] }}
                      >
                        {column.name[lang]}
                      </Typography>

                      <MLink
                        component="button"
                        underline="hover"
                        onClick={() => goToProduct(row)}
                        sx={{
                          textAlign: "right",
                          flex: 1,
                          wordBreak: "break-word",
                          cursor: "pointer",
                          color: "primary.main",
                          fontWeight: 700,
                          fontFamily: font[lang],
                        }}
                      >
                        {row.productName}
                      </MLink>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontFamily={font[lang]}
                      >
                        {column.quantity[lang]}
                      </Typography>
                      <TextField
                        size="small"
                        value={row.qty}
                        type="number"
                        inputProps={{
                          min: 1,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        onChange={(e) =>
                          changeQty(row.productID, e.target.value)
                        }
                        sx={{ width: 120 }}
                      />
                      <Box sx={{ flex: 1 }} />
                      <IconButton
                        color="error"
                        onClick={() => askDeleteRow(row)}
                      >
                        <DeleteOutlineRounded />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })
          )}
        </Stack>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        justifyContent="flex-end"
        sx={{ mt: 2 }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={goReference}
          fullWidth
          sx={{ maxWidth: { xs: "100%", sm: 200 } }}
        >
          {buttonText.other[lang]}
        </Button>
        <Button
          variant="contained"
          color="info"
          onClick={commitQtyChanges}
          fullWidth
          disabled={!inq || loading}
          sx={{ maxWidth: { xs: "100%", sm: 200 } }}
        >
          {buttonText.saveChange[lang]}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={goStep2}
          disabled={!items.length}
          fullWidth
          sx={{ maxWidth: { xs: "100%", sm: 200 } }}
        >
          {buttonText.cart[lang]}
        </Button>
      </Stack>

      {inq && (
        <>
          <Divider sx={{ my: 3 }} />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Typography variant="body2" color="text.secondary">
              {bottomText.inquireID[lang]} {inq.inquiryID} <br />{" "}
              {bottomText.time[lang]} {inq.inquiredDate || "-"}
            </Typography>

            <Button
              variant="text"
              color="error"
              onClick={askDeleteAll}
              sx={{ alignSelf: { xs: "flex-end", sm: "auto" } }}
              disabled={loading}
            >
              {buttonText.bulkDelete[lang]}
            </Button>
          </Stack>
        </>
      )}

      {/* Confirm: Delete row */}
      <Dialog
        open={confirmRowOpen}
        onClose={() => setConfirmRowOpen(false)}
        aria-labelledby="confirm-delete-row-title"
        sx={{ fontFamily: font[lang] }}
      >
        <DialogTitle id="confirm-delete-row-title">
          {confirmText.confirmTitle[lang]}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {confirmText.confirmRowBody[lang](pendingDeleteProductName)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRowOpen(false)}>
            {confirmText.cancel[lang]}
          </Button>
          <Button color="error" variant="contained" onClick={confirmDeleteRow}>
            {confirmText.delete[lang]}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm: Delete all */}
      <Dialog
        open={confirmAllOpen}
        onClose={() => setConfirmAllOpen(false)}
        aria-labelledby="confirm-delete-all-title"
        sx={{ fontFamily: font[lang] }}
      >
        <DialogTitle id="confirm-delete-all-title">
          {confirmText.confirmAllTitle[lang]}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {confirmText.confirmAllBody[lang]}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAllOpen(false)}>
            {confirmText.cancel[lang]}
          </Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAll}>
            {confirmText.deleteAll[lang]}
          </Button>
        </DialogActions>
      </Dialog>

      <ImageGalleryDialog
        open={imgDlgOpen}
        onClose={() => setImgDlgOpen(false)}
        title={imgDlgTitle}
        images={imgDlgImages}
      />
    </Box>
  );
}
